import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { BaseComponentProps } from '../../types/common';

export interface HelpButtonProps extends BaseComponentProps {
  message: React.ReactNode;
  icon?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'info' | 'muted';
  style?: React.CSSProperties;
}

/**
 * Enhanced Help Button with Bootstrap Tooltip
 * Provides contextual help information on hover
 */
export const HelpButton: React.FC<HelpButtonProps> = ({
  message,
  icon = 'fas fa-question-circle',
  placement = 'bottom',
  size = 'md',
  variant = 'info',
  style,
  className = '',
  id,
  'data-testid': testId,
  ...props
}) => {
  const sizeClass = size === 'sm' ? 'fa-sm' : size === 'lg' ? 'fa-lg' : '';
  const variantClass = `text-${variant}`;
  
  const tooltip = (
    <Tooltip id={`tooltip-${id || 'help'}`}>
      {message}
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement={placement}
      overlay={tooltip}
      trigger={['hover', 'focus']}
    >
      <span
        className={`help-button ${variantClass} ${className}`.trim()}
        style={{ cursor: 'help', ...style }}
        tabIndex={0}
        role="button"
        aria-label="Help information"
        id={id}
        data-testid={testId}
        {...props}
      >
        <i className={`${icon} ${sizeClass}`.trim()} aria-hidden="true" />
      </span>
    </OverlayTrigger>
  );
};

/**
 * Predefined help messages for common use cases
 */
export const HELP_MESSAGES = {
  FULL_SERVICE_STAFFING: (
    <div>
      <h6>What is Full Service Staffing?</h6>
      <p className="mb-2">This is concierge-level service so you won't lift a finger beyond giving us a call.</p>
      <p className="mb-2">Experience seamless hiring with DirectDental's staffing service package.</p>
      <ul className="ps-3 mb-0">
        <li>Dedicated Recruiter</li>
        <li>Premium Campaign</li>
        <li>Highest Priority</li>
        <li>More Applicants</li>
        <li>Custom Job Description</li>
        <li>Featured Employer Profile</li>
        <li>Candidate Screening</li>
        <li>License verification</li>
        <li>Temp Staff Support</li>
      </ul>
    </div>
  ),
  BOOSTED_JOB: (
    <div>
      <h6>What is a Boosted Job?</h6>
      <p className="mb-2">Enhanced visibility and priority placement for your job posting.</p>
      <ul className="ps-3 mb-0">
        <li>Premium Campaign</li>
        <li>Highest Priority</li>
        <li>More Applicants</li>
        <li>Custom Job Description</li>
        <li>Featured Employer Profile</li>
      </ul>
    </div>
  ),
  PAY_TRANSPARENCY: (
    <div>
      <h6>Pay Required</h6>
      <p className="mb-0">"Pay Transparency Laws" now require pay to be included on job postings.</p>
    </div>
  ),
} as const;

export default HelpButton;
